<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script>
import { mapState, mapActions } from 'vuex'
import { showSnackbar } from '@/helper'
import BaseModal from '@/components/BaseModal/index.vue'
import { useBreakpoint } from '@/app/Checkout/composables/useBreakpoint'

export default {
  name: 'FreeSamplesModal',
  components: { BaseModal },
  setup() {
    const { isMobile } = useBreakpoint()
    return { isMobile }
  },
  props: {
    modelValue: { type: Boolean, default: false },
    /** Optional width for the modal panel (e.g. '600px', '90%'). Passed to BaseModal. */
    width: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      /** beauty_deal_ids currently selected (checked) in the UI – local only until Save */
      selectedFreeSampleNo: [],
      /** snapshot of initially active selections when modal opens (used to detect local diffs) */
      initialSelectedFreeSampleNo: [],
      /** beauty_deal_ids the user has unchecked this session – removed from cart on Save */
      unselectedFreeSampleNo: [],
      saving: false,
    }
  },
  computed: {
    ...mapState('product', ['freeSamples', 'allowedFreeSamples']),
    ...mapState('cart', ['beautyDealsSuccess']),
    isOpen: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    canSelectMore() {
      const allowed = Number(this.allowedFreeSamples) || 0
      const limit = allowed > 0 ? allowed : 1
      return this.selectedFreeSampleNo.length < limit
    },
    selectedCount() {
      return this.selectedFreeSampleNo.length
    },
    hasLocalChanges() {
      const initial = Array.isArray(this.initialSelectedFreeSampleNo)
        ? this.initialSelectedFreeSampleNo
        : []
      const current = Array.isArray(this.selectedFreeSampleNo) ? this.selectedFreeSampleNo : []

      const initialSet = new Set(initial.map((v) => String(v)))
      const currentSet = new Set(current.map((v) => String(v)))

      if (initialSet.size !== currentSet.size) return true
      for (const id of currentSet) {
        if (!initialSet.has(id)) return true
      }
      return false
    },
  },
  watch: {
    isOpen: {
      async handler(open) {
        if (open) {
          await this.getFreeSamples()
          this.syncSelectionFromStore()
        }
      },
      immediate: true,
    },
    beautyDealsSuccess: {
      handler(success) {
        if (success?.msg) {
          showSnackbar('success', 'iconfont-check', success.msg)
          this.$store.commit('cart/REMOVE_BEAUTY_DEALS_SUCCESS')
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this._unsubscribeFreeSamples = this.$store.subscribe((mutation) => {
      if (mutation.type === 'product/SET_FREE_SAMPLES' && this.isOpen) {
        this.syncSelectionFromStore()
      }
    })
  },
  beforeUnmount() {
    if (this._unsubscribeFreeSamples) this._unsubscribeFreeSamples()
  },
  methods: {
    ...mapActions('product', ['getFreeSamples']),
    ...mapActions('cart', ['addBeautyDeal', 'removeBeautyDeal']),
    getBeautyDealId(product) {
      return product.beauty_deal_id ?? product.id
    },
    isSelected(product) {
      const id = this.getBeautyDealId(product)
      return this.selectedFreeSampleNo.some((sid) => sid == id)
    },
    /** Disabled when at limit and this sample is not selected */
    isRowDisabled(product) {
      const id = this.getBeautyDealId(product)
      if (id == null) return true
      if (this.selectedFreeSampleNo.some((sid) => sid == id)) return false
      const allowed = Number(this.allowedFreeSamples) || 0
      if (allowed <= 0) return false
      return this.selectedFreeSampleNo.length >= allowed
    },
    /** Toggle: update only local state (selectedFreeSampleNo / unselectedFreeSampleNo); no API until Save */
    handleRowClick(product) {
      if (this.isRowDisabled(product)) return
      this.handleFreeSamplePress(product)
    },
    handleFreeSamplePress(product) {
      const id = this.getBeautyDealId(product)
      if (id == null) return
      const checked = this.selectedFreeSampleNo.some((sid) => sid == id)
      if (checked) {
        this.selectedFreeSampleNo = this.selectedFreeSampleNo.filter((sid) => sid != id)
        this.unselectedFreeSampleNo = this.unselectedFreeSampleNo.includes(id)
          ? this.unselectedFreeSampleNo
          : [...this.unselectedFreeSampleNo, id]
      } else {
        if (!this.canSelectMore) return
        this.selectedFreeSampleNo = this.selectedFreeSampleNo.includes(id)
          ? this.selectedFreeSampleNo
          : [...this.selectedFreeSampleNo, id]
        this.unselectedFreeSampleNo = this.unselectedFreeSampleNo.filter((sid) => sid != id)
      }
    },
    /** Pre-select active free samples from current store only (no refetch on open – data already loaded with cart). */
    syncSelectionFromStore() {
      const products = this.$store.state.product?.freeSamples ?? []
      if (!Array.isArray(products)) return
      this.selectedFreeSampleNo = products
        .filter((p) => p.is_active === true)
        .map((p) => p.beauty_deal_id ?? p.id)
        .filter(Boolean)
      // Create a snapshot so we can tell whether the user has changed anything locally.
      this.initialSelectedFreeSampleNo = [...this.selectedFreeSampleNo]
      this.unselectedFreeSampleNo = []
    },
    async save() {
      if (this.saving) return
      this.saving = true
      try {
        if (this.unselectedFreeSampleNo.length) {
          await this.removeBeautyDeal(this.unselectedFreeSampleNo)
        }
        if (this.selectedFreeSampleNo.length) {
          await this.addBeautyDeal(this.selectedFreeSampleNo)
        }
        await this.$store.dispatch('cart/getCart')
        await this.getFreeSamples()
        this.syncSelectionFromStore()
        this.isOpen = false
      } finally {
        this.saving = false
      }
    },
    close() {
      this.isOpen = false
    },
    brandName(product) {
      return product.brand?.name || product.brand_name || ''
    },
    productDescription(product) {
      return product.name || product.title || product.description || ''
    },
    /** Resolve image URL (prepend base URL if relative and env is set) */
    imageUrl(thumb) {
      if (!thumb) return ''
      const base = import.meta.env.VITE_IMAGE_BASE_URL || ''
      if (base && thumb.startsWith('/')) return base.replace(/\/$/, '') + thumb
      if (base && !thumb.startsWith('http')) return base.replace(/\/$/, '') + '/' + thumb.replace(/^\//, '')
      return thumb
    },
  },
}
</script>
